export const ROUTE_ID_PLACEHOLDER = ":id";

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const ISO_DATE_SEGMENT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const NUMERIC_SEGMENT_PATTERN = /^\d+$/;
const HEX_SEGMENT_PATTERN = /^[0-9a-f]{16,}$/i;
const OPAQUE_TOKEN_SEGMENT_PATTERN = /^[A-Za-z0-9_-]{20,}$/;
const UPPERCASE_PATTERN = /[A-Z]/;
const EMAIL_SEGMENT_PATTERN = /^[^@\s]+@[^@\s]+$/;
const SCHEME_SEPARATOR = "://";
const URL_PROPERTY_NAMES = ["url", "pathname", "referrer"];

const isIdentifierSegment = (segment: string): boolean => {
	if (UUID_PATTERN.test(segment)) {
		return true;
	}
	if (ISO_DATE_SEGMENT_PATTERN.test(segment) || NUMERIC_SEGMENT_PATTERN.test(segment) || HEX_SEGMENT_PATTERN.test(segment)) {
		return true;
	}
	if (EMAIL_SEGMENT_PATTERN.test(segment)) {
		return true;
	}
	return OPAQUE_TOKEN_SEGMENT_PATTERN.test(segment) && UPPERCASE_PATTERN.test(segment);
};

const toPathPattern = (path: string): string =>
	path
		.split("/")
		.map((segment) => (isIdentifierSegment(segment) ? ROUTE_ID_PLACEHOLDER : segment))
		.join("/");

export const toRoutePattern = (value: string): string => {
	const withoutFragment = value.split("#")[0] ?? "";
	const withoutQuery = withoutFragment.split("?")[0] ?? "";
	const schemeIndex = withoutQuery.indexOf(SCHEME_SEPARATOR);
	if (schemeIndex === -1) {
		return toPathPattern(withoutQuery);
	}
	const pathIndex = withoutQuery.indexOf("/", schemeIndex + SCHEME_SEPARATOR.length);
	if (pathIndex === -1) {
		return withoutQuery;
	}
	return `${withoutQuery.slice(0, pathIndex)}${toPathPattern(withoutQuery.slice(pathIndex))}`;
};

const isUrlPropertyKey = (key: string): boolean => {
	const name = key.startsWith("$") ? key.slice(1) : key;
	return URL_PROPERTY_NAMES.some((suffix) => name === suffix || name.endsWith(`_${suffix}`));
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

export const sanitizeUrlProperties = (
	properties: Record<string, unknown>,
	additionalKeys: readonly string[] = [],
): Record<string, unknown> => {
	const sanitized: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(properties)) {
		const isSanitizableKey = isUrlPropertyKey(key) || additionalKeys.includes(key);
		if (typeof value === "string") {
			sanitized[key] = isSanitizableKey ? toRoutePattern(value) : value;
			continue;
		}
		sanitized[key] = isPlainRecord(value) ? sanitizeUrlProperties(value, additionalKeys) : value;
	}
	return sanitized;
};
